"use client";

import { Drawer as DrawerPrimitive } from "@base-ui/react/drawer";
import { mergeProps } from "@base-ui/react/merge-props";
import { useRender } from "@base-ui/react/use-render";
import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { AncestorLayerContext } from "../../lib/backdrop-layer";
import { useIsTouchDevice } from "../../hooks/use-is-touch-device.ts";
import { isEditable } from "../../hooks/use-keyboard-anchor-follow.ts";
import { useTouchAwareInitialFocus } from "../../hooks/use-open-focus";
import { useEnterOnMount } from "../../hooks/use-enter-on-mount.ts";
import { Icon } from "./icon";
import { cva, type VariantProps } from "../../lib/cva";
import { cn } from "../../lib/utils";
import { Button } from "./button";
import { ScrollArea } from "./scroll-area";
import { type SurfaceToken } from "./box-classes";
import type { Omittable } from "../../lib/props";

type DrawerPosition = "right" | "left" | "top" | "bottom";

/**
 * The two heights a bottom or top drawer takes — named, never a fraction, so
 * every drawer on a phone opens to one of two shapes. Both fill the height
 * the viewport gives a drawer (short content never makes a short drawer):
 *
 *   half   rests at the middle of the screen and drags up to the top — a
 *          quick task: a picker, a field's editor, a small form
 *   full   the top alone — a surface worked in: a detail view, search,
 *          filters, a settings panel
 */
export type DrawerSize = "half" | "full";

// Fractions of the viewport. `1` clamps to the filled popup's own height (the
// viewport less `--drawer-gutter`), so the top stop shows the whole popup.
const HALF_STOP = 0.5;
/** The share of the viewport height a `half` bottom drawer covers at rest. */
export const DRAWER_HALF_STOP = HALF_STOP;
const TOP_STOP = 1;
const HALF_SNAP_POINTS = [HALF_STOP, TOP_STOP];

type DrawerContextValue = {
  position: DrawerPosition;
  /** Lifts a `half` drawer to its top stop; `null` on any other drawer. */
  raise: (() => void) | null;
  /** `true` from the open transition's end to the close transition's end. */
  settled: React.RefObject<boolean>;
  /** A field that let go of the focus it took while the drawer was still
   *  opening; the drawer hands it back once it has settled. */
  deferredFocus: React.RefObject<HTMLElement | null>;
};

const DrawerContext: React.Context<DrawerContextValue> =
  createContext<DrawerContextValue>({
    position: "bottom",
    raise: null,
    settled: { current: false },
    deferredFocus: { current: null },
  });

const directionMap: Record<
  DrawerPosition,
  DrawerPrimitive.Root.Props["swipeDirection"]
> = {
  bottom: "down",
  left: "left",
  right: "right",
  top: "up",
};

export type DrawerProps = Omit<
  DrawerPrimitive.Root.Props,
  "snapPoints" | "snapPoint" | "defaultSnapPoint" | "onSnapPointChange"
> &
  Omittable<{
    position?: DrawerPosition;
    /** The drawer's height — see `DrawerSize`. Bottom and top drawers only;
     *  the side positions stand full height. Default `half`. */
    size?: DrawerSize;
    /** `half` only: open at the top stop. For a drawer that focuses a text
     *  field once it has settled — the keyboard takes the lower half anyway,
     *  and opening low would only jump. A field focused AS the drawer opens
     *  (`autoFocus`) lifts it on its own. */
    opensAtTop?: boolean;
  }>;

export function Drawer({
  swipeDirection,
  position = "bottom",
  size = "half",
  opensAtTop = false,
  onOpenChangeComplete,
  open: openProp,
  ...props
}: DrawerProps): React.ReactElement {
  // A drawer mounted already open still slides in — see useEnterOnMount.
  const open = useEnterOnMount(openProp);
  const halfSize = size === "half" && (position === "bottom" || position === "top");
  const openStop = opensAtTop ? TOP_STOP : HALF_STOP;
  // The stop is held here so a focused field can lift the drawer (`raise`);
  // a swipe between the stops reports through `onSnapPointChange`. It
  // resets once the drawer has closed, so every open starts at `openStop`.
  const [snapPoint, setSnapPoint] = useState<number | string | null>(openStop);
  // A finger's tap on a field is also the start of Base UI's swipe gesture,
  // and the gesture's zero-travel release reports the stop the drawer was
  // at — after the same tap's focus asked for the top. The raise is held for
  // one frame and that release is refused; a real swipe never lands in the
  // raise's own frame.
  const raisePending = useRef(false);
  const raise = useCallback(() => {
    raisePending.current = true;
    setSnapPoint(TOP_STOP);
    requestAnimationFrame(() => {
      raisePending.current = false;
    });
  }, []);
  const handleSnapPointChange: NonNullable<
    DrawerPrimitive.Root.Props["onSnapPointChange"]
  > = useCallback((next, details) => {
    if (raisePending.current && details.reason === "swipe" && next !== TOP_STOP) {
      details.cancel();
      return;
    }
    setSnapPoint(next);
  }, []);
  const settled = useRef(false);
  const deferredFocus = useRef<HTMLElement | null>(null);
  const context = useMemo(
    () => ({ position, raise: halfSize ? raise : null, settled, deferredFocus }),
    [position, halfSize, raise],
  );
  const handleOpenChangeComplete = useCallback(
    (open: boolean) => {
      settled.current = open;
      // The field that let go of its opening-time focus (see DrawerPopup)
      // takes it back now that the drawer stands still: the keyboard rises
      // over a settled sheet, and the field is above it at the top stop.
      const target = deferredFocus.current;
      deferredFocus.current = null;
      if (open) {
        if (target?.isConnected) target.focus({ preventScroll: true });
      } else {
        setSnapPoint(openStop);
      }
      onOpenChangeComplete?.(open);
    },
    [openStop, onOpenChangeComplete],
  );

  return (
    <DrawerContext.Provider value={context}>
      <DrawerPrimitive.Root
        swipeDirection={swipeDirection ?? directionMap[position]}
        {...(halfSize
          ? {
              snapPoints: HALF_SNAP_POINTS,
              snapPoint,
              onSnapPointChange: handleSnapPointChange,
            }
          : null)}
        onOpenChangeComplete={handleOpenChangeComplete}
        {...props}
        open={open}
      />
    </DrawerContext.Provider>
  );
}

export const DrawerPortal: typeof DrawerPrimitive.Portal =
  DrawerPrimitive.Portal;

export function DrawerTrigger(
  props: DrawerPrimitive.Trigger.Props,
): React.ReactElement {
  return <DrawerPrimitive.Trigger data-slot="drawer-trigger" {...props} />;
}

export function DrawerClose(
  props: DrawerPrimitive.Close.Props,
): React.ReactElement {
  return <DrawerPrimitive.Close data-slot="drawer-close" {...props} />;
}

export const drawerSwipeAreaVariants = cva("fixed z-50 touch-none", {
  variants: {
    position: {
      bottom: "inset-x-0 bottom-0 h-8",
      top: "inset-x-0 top-0 h-8",
      left: "inset-y-0 left-0 w-8",
      right: "inset-y-0 right-0 w-8",
    } satisfies Record<DrawerPosition, string>,
  },
});

export function DrawerSwipeArea({
  className,
  position: positionProp,
  ...props
}: DrawerPrimitive.SwipeArea.Props & Omittable<{
  position?: DrawerPosition;
}>): React.ReactElement {
  const { position: contextPosition } = useContext(DrawerContext);
  const position = positionProp ?? contextPosition;

  return (
    <DrawerPrimitive.SwipeArea
      className={cn(drawerSwipeAreaVariants({ position }), className)}
      data-slot="drawer-swipe-area"
      {...props}
    />
  );
}

export function DrawerBackdrop({
  className,
  scrim = true,
  ...props
}: DrawerPrimitive.Backdrop.Props & Omittable<{
  /** `false` leaves the backdrop unpainted: the page stays at full brightness
   *  behind the drawer, and the backdrop is only the dismiss target — a press
   *  beside the drawer still closes it. For a drawer that annotates what it
   *  covers rather than replacing it, the way a place sheet sits on a map. */
  scrim?: boolean;
}>): React.ReactElement {
  // Stacked directly on another drawer, the parent drawer carries the scrim:
  // it lays the same 32% wash over its own face (`drawer-stack-scrim`) while
  // a child is open, and a second full-screen wash on top would double the
  // dim on the page — so the backdrop stays off there (the Base UI default
  // for a nested layer). Over any other layer — a dialog, a sheet, a
  // backdrop-less panel — the drawer paints its own. See AncestorLayerContext.
  const stackedOnDrawer = useContext(AncestorLayerContext) === "drawer";
  return (
    <DrawerPrimitive.Backdrop
      forceRender={!stackedOnDrawer}
      className={cn(
        "fixed inset-0 z-50 opacity-[calc(1-var(--drawer-swipe-progress))] transition-opacity duration-450 ease-drawer data-ending-style:opacity-0 data-starting-style:opacity-0 data-ending-style:duration-[calc(var(--drawer-swipe-strength)*400ms)] data-swiping:duration-0 supports-[-webkit-touch-callout:none]:absolute",
        scrim && "bg-black/32 backdrop-blur-sm",
        className,
      )}
      data-slot="drawer-backdrop"
      {...props}
    />
  );
}

export const drawerViewportVariants = cva(
  [
    // `--drawer-gutter` is the strip the viewport leaves above a bottom drawer
    // (below a top one): the page showing through, and the dismiss target.
    // A bottom or top popup sizes itself against it, so the two never disagree.
    "fixed inset-0 z-50 [--bleed:--spacing(12)] [--drawer-gutter:--spacing(12)] [--inset:--spacing(0)]",
    "touch-none",
  ],
  {
    compoundVariants: [
      // The inset gutter stops at the drawer's own edge: no top gutter above
      // a top drawer, no bottom gutter below a bottom drawer.
      {
        class: "pt-(--inset)",
        position: ["top", "left", "right"],
        variant: "inset",
      },
      {
        class: "pb-(--inset)",
        position: ["bottom", "left", "right"],
        variant: "inset",
      },
    ],
    defaultVariants: {
      variant: "default",
    },
    variants: {
      position: {
        bottom: "grid grid-rows-[1fr_auto] pt-(--drawer-gutter)",
        top: "grid grid-rows-[auto_1fr] pb-(--drawer-gutter)",
        left: "flex justify-start",
        right: "flex justify-end",
      } satisfies Record<DrawerPosition, string>,
      variant: {
        default: "",
        straight: "",
        inset: "px-(--inset) sm:[--inset:--spacing(4)]",
      },
    },
  },
);

export function DrawerViewport({
  className,
  position,
  variant,
  ...props
}: DrawerPrimitive.Viewport.Props & Omittable<{
  position?: DrawerPosition;
  variant?: VariantProps<typeof drawerViewportVariants>["variant"];
}>): React.ReactElement {
  return (
    <DrawerPrimitive.Viewport
      className={cn(drawerViewportVariants({ position, variant }), className)}
      data-slot="drawer-viewport"
      {...props}
    />
  );
}

export const drawerPopupVariants = cva(
  [
    // The fill travels as --drawer-surface (set by the `surface` variant) so
    // the popup and its overscroll bleed (`after:`) read one value. Only
    // `transform` transitions: a sheet receding under a stacked child moves
    // on the compositor alone. Height, box-shadow and background-color
    // tweens each repainted the whole sheet per frame and made the stack
    // stutter on a phone; a nested child's swipe drives the recession
    // directly (`data-nested-drawer-swiping` zeroes the duration), the way
    // the sheet's own swipe already does.
    "group/popup relative flex max-h-full min-h-0 w-full min-w-0 flex-col bg-(--drawer-surface) not-dark:bg-clip-padding outline-none transition-transform duration-450 ease-drawer will-change-transform data-nested-drawer-swiping:duration-0 [--peek:calc(--spacing(6)-1px)] [--scale-base:calc(max(0,1-(var(--nested-drawers)*var(--stack-step))))] [--scale:clamp(0,calc(var(--scale-base)+(var(--stack-step)*var(--stack-progress))),1)] [--stack-peek-offset:max(0px,calc((var(--nested-drawers)-var(--stack-progress))*var(--peek)))] [--stack-progress:clamp(0,var(--drawer-swipe-progress),1)] [--stack-step:0.05] before:pointer-events-none before:absolute before:inset-0 before:shadow-[0_1px_--theme(--color-black/4%)] after:pointer-events-none after:absolute after:bg-(--drawer-surface) data-swiping:select-none data-nested-drawer-open:overflow-hidden data-ending-style:duration-[calc(var(--drawer-swipe-strength)*400ms)] dark:before:shadow-[0_-1px_--theme(--color-white/6%)]",
    "touch-none",
  ],
  {
    compoundVariants: [
      // Directional depth off the OPEN edge, on every surface. The token
      // surface shadows (`shadow-elevated` and kin) drop DOWN, which along a
      // bottom drawer's top edge paints nothing — and in light mode the
      // default `elevated` fill is the same white as the page card behind
      // it, so without this the hairline is the sheet's only boundary. The
      // page scrim carries a full-height sheet; a half sheet, and any drawer
      // raised over another drawer (no scrim of its own, see DrawerBackdrop),
      // needs the edge to read on its own. Physical offsets because
      // box-shadow has no logical direction. `inset` floats with its own
      // chrome. SheetPopup keeps the quieter rung: a side panel on a
      // desktop, not a phone sheet.
      {
        class:
          "shadow-[0_-12px_24px_-12px_--theme(--color-black/10%)] dark:shadow-[0_-12px_24px_-12px_--theme(--color-black/40%)]",
        position: "bottom",
        variant: ["default", "straight"],
      },
      {
        class:
          "shadow-[0_12px_24px_-12px_--theme(--color-black/10%)] dark:shadow-[0_12px_24px_-12px_--theme(--color-black/40%)]",
        position: "top",
        variant: ["default", "straight"],
      },
      {
        class:
          "shadow-[12px_0_24px_-12px_--theme(--color-black/10%)] dark:shadow-[12px_0_24px_-12px_--theme(--color-black/40%)]",
        position: "left",
        variant: ["default", "straight"],
      },
      {
        class:
          "shadow-[-12px_0_24px_-12px_--theme(--color-black/10%)] dark:shadow-[-12px_0_24px_-12px_--theme(--color-black/40%)]",
        position: "right",
        variant: ["default", "straight"],
      },
      // Without a scrim the shadow is the whole separation — the page
      // behind stays at full brightness — so it steps up one rung. Only a
      // bottom drawer opens scrimless (a map's place sheet); see `scrim`.
      {
        class:
          "shadow-[0_-16px_40px_-12px_--theme(--color-black/18%)] dark:shadow-[0_-16px_40px_-12px_--theme(--color-black/56%)]",
        position: "bottom",
        scrim: false,
        variant: ["default", "straight"],
      },
      // Corner rounding, every variant except `straight`.
      {
        class: "rounded-t-2xl",
        position: "bottom",
        variant: ["default", "inset"],
      },
      {
        class:
          "rounded-b-xl **:data-[slot=drawer-footer]:rounded-b-[calc(var(--radius-xl)-1px)]",
        position: "top",
        variant: ["default", "inset"],
      },
      {
        class:
          "rounded-e-xl **:data-[slot=drawer-footer]:rounded-ee-[calc(var(--radius-xl)-1px)]",
        position: "left",
        variant: ["default", "inset"],
      },
      {
        class:
          "rounded-s-xl **:data-[slot=drawer-footer]:rounded-es-[calc(var(--radius-xl)-1px)]",
        position: "right",
        variant: ["default", "inset"],
      },
      // The hairline overlay follows the popup's rounded corner.
      {
        class: "before:rounded-t-[calc(var(--radius-xl)-1px)]",
        position: "bottom",
        variant: "default",
      },
      {
        class: "before:rounded-b-[calc(var(--radius-xl)-1px)]",
        position: "top",
        variant: "default",
      },
      {
        class: "before:rounded-e-[calc(var(--radius-xl)-1px)]",
        position: "left",
        variant: "default",
      },
      {
        class: "before:rounded-s-[calc(var(--radius-xl)-1px)]",
        position: "right",
        variant: "default",
      },
      // Every bottom or top drawer takes every pixel the viewport gives a
      // drawer, whatever its content — its `size` stop decides how much of
      // it shows, so short content never makes a short drawer. The side
      // positions already stand full height. Under a stacked child the
      // drawer KEEPS its height: `--height` (the frontmost drawer's) and
      // `--own` (this one's) only feed the transform below, which places the
      // scaled top edge one peek past the child's. Base UI's reference tweens
      // the parent's height to the frontmost's instead — a layout animation
      // on a full-height sheet, every frame — and that is what stuttered.
      {
        class:
          "h-[calc(100dvh-var(--drawer-gutter))] [--height:max(0px,calc(var(--drawer-frontmost-height,var(--drawer-height))))] [--own:var(--drawer-height,var(--height))]",
        position: ["bottom", "top"],
      },
      // The bottom position's negative snap-offset margin cancels the box
      // growth its offset padding causes on a content-sized box. The popup's
      // box is definite — padding never grows it — so the margin would only
      // shrink the viewport's auto grid row, `max-h-full` re-resolves against
      // that smaller row, the shrunken box re-measures to a zero snap
      // offset, and the row springs back: a visible oscillation at the half
      // stop. The offset padding stays (it is what keeps the content, footer
      // included, above the fold at the half stop); only the margin goes.
      {
        class: "mb-0",
        position: "bottom",
      },
      {
        class:
          "data-nested-drawer-open:transform-[translateY(calc(var(--drawer-swipe-movement-y)-var(--stack-peek-offset)-(var(--height)-var(--scale)*var(--own))))_scale(var(--scale))] origin-[50%_calc(100%-var(--inset))]",
        position: "bottom",
      },
      {
        class:
          "data-nested-drawer-open:transform-[translateY(calc(var(--drawer-swipe-movement-y)+var(--stack-peek-offset)+(var(--height)-var(--scale)*var(--own))))_scale(var(--scale))] origin-[50%_var(--inset)]",
        position: "top",
      },
      {
        class:
          "data-nested-drawer-open:transform-[translateX(calc(var(--drawer-swipe-movement-x)+var(--stack-peek-offset)))_scale(var(--scale))] origin-right",
        position: "left",
      },
      {
        class:
          "data-nested-drawer-open:transform-[translateX(calc(var(--drawer-swipe-movement-x)-var(--stack-peek-offset)))_scale(var(--scale))] origin-left",
        position: "right",
      },
    ],
    defaultVariants: {
      // `elevated`, not `popover`: a drawer is a room, and dark popovers lift
      // past the card rung for transient popups — see `dialogPopupVariants`.
      scrim: true,
      surface: "elevated",
      variant: "default",
    },
    variants: {
      // Whether the backdrop dims the page — the popup's edge shadow steps
      // up when it does not (see the compound variants). The classes live on
      // the backdrop, not here.
      scrim: { false: "", true: "" },
      // The surface the popup paints: the fill via --drawer-surface, the ink
      // directly. Same axis as SheetPopup's, less the token shadow: every
      // drawer carries the directional edge shadow instead (compound
      // variants above), which would only clobber it.
      surface: {
        sunken: "[--drawer-surface:var(--sunken)] text-foreground",
        background: "[--drawer-surface:var(--background)] text-foreground",
        card: "[--drawer-surface:var(--card)] text-card-foreground",
        elevated: "[--drawer-surface:var(--elevated)] text-foreground",
        popover: "[--drawer-surface:var(--popover)] text-popover-foreground",
        sidebar: "[--drawer-surface:var(--sidebar)] text-sidebar-foreground",
      } satisfies Record<SurfaceToken, string>,
      position: {
        bottom:
          "transform-[translateY(calc(var(--drawer-snap-point-offset)+var(--drawer-swipe-movement-y)))] data-ending-style:transform-[translateY(calc(100%+env(safe-area-inset-bottom,0px)+var(--inset)))] data-starting-style:transform-[translateY(calc(100%+env(safe-area-inset-bottom,0px)+var(--inset)))] row-start-2 -mb-[max(0px,calc(var(--drawer-snap-point-offset,0px)+clamp(0,1,var(--drawer-snap-point-offset,0px)/1px)*var(--drawer-swipe-movement-y,0px)))] border-t pb-[max(0px,calc(env(safe-area-inset-bottom,0px)+var(--drawer-snap-point-offset,0px)+clamp(0,1,var(--drawer-snap-point-offset,0px)/1px)*var(--drawer-swipe-movement-y,0px)))] not-data-starting-style:not-data-ending-style:transition-[transform,margin,padding] after:inset-x-0 after:top-full after:h-(--bleed) has-data-[slot=drawer-bar]:pt-2 data-ending-style:mb-0 data-starting-style:mb-0 data-ending-style:pb-0 data-starting-style:pb-0",
        top: "data-starting-style:transform-[translateY(calc(-100%-var(--inset)))] data-ending-style:transform-[translateY(calc(-100%-var(--inset)))] transform-[translateY(var(--drawer-swipe-movement-y))] border-b after:inset-x-0 after:bottom-full after:h-(--bleed) has-data-[slot=drawer-bar]:pb-2",
        left: "data-starting-style:transform-[translateX(calc(-100%-var(--inset)))] data-ending-style:transform-[translateX(calc(-100%-var(--inset)))] transform-[translateX(var(--drawer-swipe-movement-x))] w-[calc(100%-(--spacing(12)))] max-w-md border-e after:inset-y-0 after:end-full after:w-(--bleed) has-data-[slot=drawer-bar]:pe-2",
        right:
          "transform-[translateX(var(--drawer-swipe-movement-x))] data-ending-style:transform-[translateX(calc(100%+var(--inset)))] data-starting-style:transform-[translateX(calc(100%+var(--inset)))] col-start-2 w-[calc(100%-(--spacing(12)))] max-w-md border-s after:inset-y-0 after:start-full after:w-(--bleed) has-data-[slot=drawer-bar]:ps-2",
      } satisfies Record<DrawerPosition, string>,
      variant: {
        default: "",
        inset:
          "before:hidden sm:rounded-xl sm:border sm:after:bg-transparent sm:before:rounded-[calc(var(--radius-xl)-1px)] sm:**:data-[slot=drawer-footer]:rounded-b-[calc(var(--radius-xl)-1px)]",
        straight: "[--stack-step:0]",
      },
    },
  },
);

export function DrawerPopup({
  className,
  children,
  showCloseButton = false,
  position: positionProp,
  variant,
  surface,
  showBar = false,
  scrim = true,
  cornerActions,
  portalProps,
  initialFocus,
  onFocus,
  onPointerDownCapture,
  ref,
  ...props
}: DrawerPrimitive.Popup.Props & Omittable<{
  showCloseButton?: boolean;
  /** `false` opens the drawer over an undimmed page — see `DrawerBackdrop`.
   *  The drawer's edge shadow steps up to carry the separation alone. */
  scrim?: boolean;
  /** Icon-only `icon-xs` utility buttons in the top corner, before the
   *  close — `DialogPopup`'s corner, so a dialog keeps its utilities when
   *  it presents as a drawer on a phone. */
  cornerActions?: React.ReactNode;
  position?: DrawerPosition;
  variant?: VariantProps<typeof drawerPopupVariants>["variant"];
  /**
   * The surface the drawer paints. The default reads as a floating panel,
   * like a dialog. Name a quieter surface for a drawer that is the mobile
   * presentation of a full page — a detail screen whose desktop shape is a
   * dialog over the page — so its content sits on the page fill in both.
   */
  surface?: SurfaceToken;
  showBar?: boolean;
  portalProps?: DrawerPrimitive.Portal.Props;
}>): React.ReactElement {
  const { position: contextPosition, raise, settled, deferredFocus } = useContext(DrawerContext);
  const position = positionProp ?? contextPosition;
  // A coarse-pointer open lands on the popup, not on the first field inside
  // it (Base UI reads the click, which WebKit stamps "mouse" for a finger);
  // a field that claimed focus by its own `autoFocus` keeps it.
  const touchFocus = useTouchAwareInitialFocus<HTMLDivElement>(ref);
  // A field focused in a `half` drawer lifts it to the top stop, so the
  // software keyboard rising over the lower half never covers the field or
  // what it lists. Touch only: a mouse brings no keyboard.
  const isTouch = useIsTouchDevice();
  // A pointer that lands inside the popup: the focus that follows it is the
  // user's, never deferred, and it outranks a deferred field.
  const pointerSeen = useRef(false);
  const handlePointerDownCapture: NonNullable<
    DrawerPrimitive.Popup.Props["onPointerDownCapture"]
  > = (event) => {
    pointerSeen.current = true;
    deferredFocus.current = null;
    onPointerDownCapture?.(event);
  };
  const keyboardProxyRef = useRef<HTMLInputElement>(null);
  const handleFocus: NonNullable<DrawerPrimitive.Popup.Props["onFocus"]> = (event) => {
    if (isTouch && isEditable(event.target)) {
      // A field focused before the drawer has settled, with no pointer yet
      // inside the popup, took focus by its own `autoFocus` in the commit
      // that mounted the popup below the screen. iOS raises the keyboard for
      // it there and scrolls the whole fixed layer to reveal it, so the
      // settled sheet lands past the top of the screen. The field hands its
      // focus to the keyboard proxy — an input at the top of the screen,
      // nothing to reveal, and a focus passed input to input keeps the
      // keyboard rising — the drawer opens straight to its top stop, and the
      // field takes focus back once the drawer has settled: the `sheetFocus`
      // grammar, applied by the host so every sheet with an autofocused
      // field gets it. Without a proxy the field simply lets go.
      if (!settled.current && !pointerSeen.current) {
        deferredFocus.current = event.target;
        const proxy = keyboardProxyRef.current;
        if (proxy) {
          proxy.inputMode = event.target.inputMode;
          proxy.focus({ preventScroll: true });
        } else {
          event.target.blur();
        }
      }
      raise?.();
    }
    onFocus?.(event);
  };
  // Base UI's own initial focus stands down while a deferred field waits,
  // so it never pulls the keyboard's focus off the proxy.
  const resolveInitialFocus: typeof touchFocus.initialFocus = (openType) =>
    deferredFocus.current ? false : touchFocus.initialFocus(openType);

  return (
    // The software keyboard on iOS Safari never shrinks the layout viewport
    // (the `interactive-widget=resizes-content` meta is ignored there), so a
    // full-height drawer keeps its bottom edge under the keys and a form
    // scroller inside it runs out of travel at the fold. The provider
    // measures the keyboard from the visual viewport, pads the focused
    // field's nearest scroller by the overlap so the rest of the form can be
    // reached, scrolls the field into the visible strip, and restores
    // everything on blur or close. It also focuses synchronously inside the
    // touch gesture (the only way iOS raises the keyboard) without the page
    // jump WebKit adds on a transformed popup. It exposes the measured inset
    // as `--drawer-keyboard-inset` on the viewport for a control that must
    // ride the keyboard's top edge; a plain footer stays under the keys, as
    // native forms do.
    <DrawerPrimitive.VirtualKeyboardProvider>
      <DrawerPortal {...portalProps}>
        <DrawerBackdrop scrim={scrim} />
        {/* A nested drawer reads this to withhold its backdrop — this popup's
            stack scrim (below) dims it instead. Every other layer kind paints
            its own over this drawer. */}
        <AncestorLayerContext.Provider value="drawer">
          <DrawerViewport position={position} variant={variant}>
            {isTouch && (
              // The keyboard proxy (see handleFocus): focusable, never
              // visible, never a tab stop, pinned to the viewport's top so
              // the keyboard has nothing to scroll into view.
              <input
                ref={keyboardProxyRef}
                aria-hidden="true"
                autoComplete="off"
                className="sr-only start-0 top-0"
                data-slot="drawer-keyboard-proxy"
                tabIndex={-1}
                type="text"
              />
            )}
            <DrawerPrimitive.Popup
              className={cn(
                drawerPopupVariants({ position, scrim, surface, variant }),
                className,
              )}
              data-slot="drawer-popup"
              initialFocus={initialFocus ?? resolveInitialFocus}
              ref={touchFocus.ref}
              onFocus={handleFocus}
              onPointerDownCapture={handlePointerDownCapture}
              {...props}
            >
              {children}
              {(showCloseButton || cornerActions) && (
                <div
                  className="absolute end-4 top-4 flex items-center gap-1"
                  data-slot="drawer-corner"
                >
                  {cornerActions}
                  {showCloseButton && (
                    <DrawerPrimitive.Close
                      aria-label="Close"
                      render={<Button size="icon-xs" color="neutral" />}
                    >
                      <Icon name="x" />
                    </DrawerPrimitive.Close>
                  )}
                </div>
              )}
              {showBar && <DrawerBar />}
              {/* The stack's scrim: the page scrim's own 32% wash, laid over
                  this drawer's face while a child drawer is open, so the
                  receded sheet dims exactly as the page behind it does — one
                  scrim at any depth, content and chrome dimmed together. An
                  overlay, never a recolor of the fill: a tinted sheet under
                  full-contrast content read as a broken surface. It fades on
                  its own opacity (compositor only) and tracks a child's swipe
                  through `--stack-progress`, with the transition off while
                  the finger drives it. Above every sticky header or footer
                  inside the panel (`z-50`, contained by the popup's
                  transform). */}
              <div
                aria-hidden="true"
                className="pointer-events-none absolute inset-0 z-50 rounded-[inherit] bg-black/32 opacity-[calc(min(1,var(--nested-drawers,0)-var(--stack-progress,0)))] transition-opacity duration-450 ease-drawer group-data-nested-drawer-swiping/popup:duration-0"
                data-slot="drawer-stack-scrim"
              />
            </DrawerPrimitive.Popup>
          </DrawerViewport>
        </AncestorLayerContext.Provider>
      </DrawerPortal>
    </DrawerPrimitive.VirtualKeyboardProvider>
  );
}

export function DrawerHeader({
  className,
  allowSelection = false,
  render,
  ...props
}: useRender.ComponentProps<"div"> & Omittable<{
  allowSelection?: boolean;
}>): React.ReactElement {
  const defaultProps = {
    className: cn(
      "flex flex-col gap-2 p-6 in-[[data-slot=drawer-popup]:has([data-slot=drawer-panel])]:pb-3 max-sm:pb-4",
      !allowSelection && "cursor-default",
      className,
    ),
    "data-slot": "drawer-header",
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render: allowSelection ? <DrawerContent render={render} /> : render,
  });
}

/** The way out on a sheet's title row: the round outline × on the leading
 *  edge. `true` closes the drawer; an object runs its own close (a step
 *  back, a draft drop) or holds the door shut mid-save. */
export type DrawerTitleRowDismiss =
  | boolean
  | Omittable<{
      /** The accessible name. Default "Close". */
      label?: string;
      onClick?: () => void;
      disabled?: boolean;
    }>;

/** The commit on a sheet's title row: always the WORD, as a round filled
 *  button — a lone glyph read as "not sure, not much feedback". */
export type DrawerTitleRowPrimary = Omittable<{
  label: string;
  onClick?: () => void;
  /** Submit this form (by id) instead of running `onClick`. */
  form?: string;
  disabled?: boolean;
  loading?: boolean;
  /** A destructive commit — the outline destructive button. */
  destructive?: boolean;
}>;

/** Every button on a sheet's title row sits on this one rung: 32px, round. */
const DRAWER_TITLE_ROW_BUTTON = { shape: "pill" } as const;

/**
 * A sheet's title row, the phone modal grammar: the way out (a round
 * outline ×) on the leading edge, the title centred, the commit as a word
 * (a round filled button) on the trailing edge. One rung — 32px — for every
 * button on the row, so no sheet picks its own.
 *
 * The children are the title column (`DrawerTitle`, a `DrawerDescription`);
 * the two clusters are the title's own line box (h-5 = the 20px title line)
 * with their buttons centred in it, so a 32px button sits on the title's
 * centreline rather than hanging from its top edge — and a title that wraps,
 * or carries a description, keeps them on its first line.
 */
export function DrawerTitleRow({
  dismiss = true,
  primary,
  actions,
  className,
  children,
}: Omittable<{
  dismiss?: DrawerTitleRowDismiss;
  primary?: DrawerTitleRowPrimary;
  /** Further round `icon`-rung buttons on the trailing edge, before the
   *  primary. */
  actions?: React.ReactNode;
  className?: string;
  children: React.ReactNode;
}>): React.ReactElement {
  const dismissConfig = typeof dismiss === "object" ? dismiss : null;
  const dismissButton =
    dismiss === false ? null : dismissConfig?.onClick ? (
      <Button
        {...DRAWER_TITLE_ROW_BUTTON}
        variant="outline"
        size="icon"
        aria-label={dismissConfig.label ?? "Close"}
        onClick={dismissConfig.onClick}
        disabled={dismissConfig.disabled}
      >
        <Icon name="x" />
      </Button>
    ) : (
      <DrawerClose
        aria-label={dismissConfig?.label ?? "Close"}
        disabled={dismissConfig?.disabled}
        render={<Button {...DRAWER_TITLE_ROW_BUTTON} variant="outline" size="icon" />}
      >
        <Icon name="x" />
      </DrawerClose>
    );
  return (
    <div
      className={cn("grid grid-cols-[1fr_auto_1fr] items-start gap-2", className)}
      data-slot="drawer-title-row"
    >
      <div className="flex h-5 items-center justify-start">{dismissButton}</div>
      <div className="flex min-w-0 flex-col items-center gap-2 text-center">{children}</div>
      <div className="flex h-5 items-center justify-end gap-1">
        {actions}
        {primary ? (
          <Button
            {...DRAWER_TITLE_ROW_BUTTON}
            onClick={primary.onClick}
            type={primary.form ? "submit" : "button"}
            form={primary.form}
            disabled={primary.disabled}
            loading={primary.loading}
            {...(primary.destructive
              ? { color: "destructive" as const, variant: "outline" as const }
              : {})}
          >
            {primary.label}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export function DrawerFooter({
  className,
  variant = "default",
  allowSelection = true,
  aboveKeyboard = false,
  render,
  ...props
}: useRender.ComponentProps<"div"> & Omittable<{
  variant?: "default" | "bare";
  allowSelection?: boolean;
  /** The footer rides the software keyboard's top edge while one is up
   *  (`--drawer-keyboard-inset`, measured by the popup's keyboard provider),
   *  never less than the home-indicator inset. Off, it stays under the keys
   *  like a native form's — right for a Cancel/Save pair beneath the field
   *  being typed in, wrong for the sheet's only actions. */
  aboveKeyboard?: boolean;
}>): React.ReactElement {
  const defaultProps = {
    className: cn(
      "flex flex-col-reverse gap-2 px-6 pb-[env(safe-area-inset-bottom,0px)] sm:flex-row sm:justify-end",
      !allowSelection && "cursor-default",
      // Without a DrawerPanel the body has no bottom gutter of its own; the
      // margin restores the gap and is inert when a panel is present.
      "in-[[data-slot=drawer-popup]:not(:has([data-slot=drawer-panel]))]:mt-4",
      variant === "default" &&
        "border-t pt-4 pb-[calc(env(safe-area-inset-bottom,0px)+--spacing(4))]",
      variant === "bare" &&
        "in-[[data-slot=drawer-popup]:has([data-slot=drawer-panel])]:pt-3 pt-4 pb-[calc(env(safe-area-inset-bottom,0px)+--spacing(6))]",
      // Spelled out per variant: Tailwind only emits a class it can read
      // whole from the source.
      aboveKeyboard &&
        variant === "default" &&
        "pb-[calc(max(env(safe-area-inset-bottom,0px),var(--drawer-keyboard-inset,0px))+--spacing(4))]",
      aboveKeyboard &&
        variant === "bare" &&
        "pb-[calc(max(env(safe-area-inset-bottom,0px),var(--drawer-keyboard-inset,0px))+--spacing(6))]",
      className,
    ),
    "data-slot": "drawer-footer",
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render: allowSelection ? <DrawerContent render={render} /> : render,
  });
}

export function DrawerTitle({
  className,
  visuallyHidden = false,
  ...props
}: DrawerPrimitive.Title.Props &
  Omittable<{
    /** Names the drawer for assistive technology without painting: the
     *  visible header is something else (a breadcrumb, a search field). */
    visuallyHidden?: boolean;
  }>): React.ReactElement {
  return (
    <DrawerPrimitive.Title
      className={cn(
        "font-heading font-semibold text-xl leading-none",
        visuallyHidden && "sr-only",
        className,
      )}
      data-slot="drawer-title"
      {...props}
    />
  );
}

export function DrawerDescription({
  className,
  visuallyHidden = false,
  ...props
}: DrawerPrimitive.Description.Props &
  Omittable<{
    /** Names the drawer for assistive technology without painting: the
     *  visible header is something else (a breadcrumb, a search field). */
    visuallyHidden?: boolean;
  }>): React.ReactElement {
  return (
    <DrawerPrimitive.Description
      className={cn(
        "text-muted-foreground text-sm",
        visuallyHidden && "sr-only",
        className,
      )}
      data-slot="drawer-description"
      {...props}
    />
  );
}

export const drawerPanelVariants = cva("", {
  defaultVariants: {
    variant: "default",
  },
  variants: {
    variant: {
      default:
        "p-6 in-[[data-slot=drawer-popup]:has([data-slot=drawer-header])]:pt-1 in-[[data-slot=drawer-popup]:has([data-slot=drawer-footer]:not(.border-t))]:pb-1",
      /** A scrolling body with NO inset of its own — the content lays its
       * own gutters, so a rail (a bare Separator) runs edge to edge. */
      flush: "p-0",
      /** A body that owns its own scrolling and layout — the drawer twin of
       * `DialogPanel`'s `workspace`. The panel supplies only the bounded flex
       * region, and never a second ScrollArea around one the body already has. */
      workspace: "flex min-h-0 flex-1 flex-col overflow-hidden p-0",
    },
  },
});

export function DrawerPanel({
  className,
  scrollFade = true,
  scrollable = true,
  allowSelection = true,
  variant,
  render,
  ...props
}: useRender.ComponentProps<"div"> & Omittable<{
  scrollFade?: boolean;
  scrollable?: boolean;
  allowSelection?: boolean;
  variant?: VariantProps<typeof drawerPanelVariants>["variant"];
}>): React.ReactElement {
  const defaultProps = {
    className: cn(
      drawerPanelVariants({ variant }),
      !allowSelection && "cursor-default",
      className,
    ),
    "data-slot": "drawer-panel",
  };

  const content = useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render: allowSelection ? <DrawerContent render={render} /> : render,
  });

  if (scrollable && variant !== "workspace") {
    return (
      <ScrollArea className="touch-auto" scrollFade={scrollFade}>
        {content}
      </ScrollArea>
    );
  }

  return content;
}

/**
 * The body of a popup that presents as a phone sheet (`Popover responsive`,
 * a `MenuSub` on a phone, the entity selector's drawer): a pinned band above
 * the drawer's one scroller, both on the sheet's 16px inset. The band holds
 * the sheet's caption (`title` — a small centred name for a sheet that lists
 * data with nothing else to say what it is: "Assignees"), any `header` the
 * host pins statically (the selector's search well and tabs) and whatever a
 * `DrawerPinned` deeper in the content portals up (a Command's search
 * field). The inset is published as `--sheet-inset` so a rule that ran edge
 * to edge in the popup keeps doing so here — a bare Separator at the body's
 * own level pulls back out of it, and `CommandSeparator bleed` adds it to
 * the list gutter it already cancels. Unset (0px) in a popup.
 */
type DrawerInsetPanelValue = {
  /** The pinned band, a portal target for `DrawerPinned`. */
  pinTarget: HTMLElement | null;
  /** Counts a pinned part in, so the band shows; returns the count-out. */
  registerPinned: () => () => void;
};

const DrawerInsetPanelContext = createContext<DrawerInsetPanelValue | null>(null);

/** `true` inside a `DrawerInsetPanel` — the body of a popover, submenu or
 *  selector that is presenting as a phone sheet. The panel is the sheet's
 *  ONE scroller, so content that would cap and scroll itself inside a popup
 *  (an inline selector's pool) renders flat here instead of nesting a second
 *  scroller under a finger's swipe, and pins its search with `DrawerPinned`. */
export function useDrawerInsetPanel(): boolean {
  return useContext(DrawerInsetPanelContext) !== null;
}

const SHEET_INSET_CLASS =
  "px-4 [--sheet-inset:--spacing(4)] *:data-[slot=separator]:-mx-(--sheet-inset) *:data-[slot=separator]:w-[calc(100%+2*var(--sheet-inset))]!";

export function DrawerInsetPanel({
  title,
  header,
  scrollable = true,
  children,
  "data-slot": dataSlot = "drawer-inset-panel",
}: Omittable<{
  /** The sheet's caption: a small centred name under the grab bar, for a
   *  sheet that lists data and would otherwise not say what it is. Omit for
   *  a sheet whose content already carries a header. */
  title?: React.ReactNode;
  /** Parts pinned above the scroller by the host itself — the selector's
   *  search well and tabs. Content deeper in the tree pins with
   *  `DrawerPinned` instead. */
  header?: React.ReactNode;
  /** `false` for a body that must not scroll — a palette sized to the
   *  sheet. Default true: the panel is the sheet's one scroller. */
  scrollable?: boolean;
  children?: React.ReactNode;
  "data-slot"?: string;
}>): React.ReactElement {
  const [pinTarget, setPinTarget] = useState<HTMLElement | null>(null);
  const [pinnedCount, setPinnedCount] = useState(0);
  const registerPinned = useCallback(() => {
    setPinnedCount((count) => count + 1);
    return () => setPinnedCount((count) => count - 1);
  }, []);
  const value = useMemo<DrawerInsetPanelValue>(
    () => ({ pinTarget, registerPinned }),
    [pinTarget, registerPinned],
  );
  const hasBand = title != null || header != null || pinnedCount > 0;
  return (
    <DrawerInsetPanelContext.Provider value={value}>
      {/* The band steps down the popup's top 24px so its first press target
          — the search well — starts under the grab bar, not beneath it. */}
      <div
        className={cn("flex shrink-0 flex-col pt-6", SHEET_INSET_CLASS, !hasBand && "hidden")}
        data-slot="drawer-inset-header"
      >
        {title != null && (
          <DrawerTitle
            className="mb-2 truncate text-center text-sm leading-5"
            data-slot="drawer-sheet-title"
          >
            {title}
          </DrawerTitle>
        )}
        {header}
        <div className="contents" ref={setPinTarget} />
      </div>
      <DrawerPanel scrollable={scrollable} variant="flush">
        <div
          className={cn("flex flex-col pb-4", hasBand ? "pt-0" : "pt-6", SHEET_INSET_CLASS)}
          data-slot={dataSlot}
        >
          {children}
        </div>
      </DrawerPanel>
    </DrawerInsetPanelContext.Provider>
  );
}

/**
 * Pins its children above a phone sheet's scroller — a search field that
 * must not scroll away under a finger — by portalling them into the
 * enclosing `DrawerInsetPanel`'s band. Anywhere else (a popover, a menu, a
 * page) it renders them in place, so a consumer wraps its search once and
 * both presentations are right.
 */
export function DrawerPinned({ children }: { children: React.ReactNode }): React.ReactElement | null {
  const panel = useContext(DrawerInsetPanelContext);
  const registerPinned = panel?.registerPinned;
  useLayoutEffect(() => registerPinned?.(), [registerPinned]);
  if (!panel) return <>{children}</>;
  if (!panel.pinTarget) return null;
  return createPortal(children, panel.pinTarget);
}

export const drawerBarVariants = cva(
  "absolute flex touch-none items-center justify-center p-3 before:rounded-full before:bg-input",
  {
    variants: {
      // The grip pill runs along the drawer's inner edge: horizontal for a
      // top/bottom drawer, vertical for a left/right one.
      position: {
        top: "inset-x-0 before:h-1 before:w-12 bottom-0",
        bottom: "inset-x-0 before:h-1 before:w-12 top-0",
        left: "inset-y-0 before:h-12 before:w-1 right-0",
        right: "inset-y-0 before:h-12 before:w-1 left-0",
      } satisfies Record<DrawerPosition, string>,
    },
  },
);

export function DrawerBar({
  className,
  position: positionProp,
  render,
  ...props
}: useRender.ComponentProps<"div"> & Omittable<{
  position?: DrawerPosition;
}>): React.ReactElement {
  const { position: contextPosition } = useContext(DrawerContext);
  const position = positionProp ?? contextPosition;
  const defaultProps = {
    "aria-hidden": true as const,
    className: cn(drawerBarVariants({ position }), className),
    "data-slot": "drawer-bar",
  };

  return useRender({
    defaultTagName: "div",
    props: mergeProps<"div">(defaultProps, props),
    render,
  });
}

export const DrawerContent: typeof DrawerPrimitive.Content =
  DrawerPrimitive.Content;
